import React, { useState, useMemo } from "react";
import {
  Box, Typography, Paper, IconButton, Button, TextField, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Select, FormControl, InputLabel, Switch, FormControlLabel,
  Tooltip, Alert, Collapse,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import EventNoteRoundedIcon from "@mui/icons-material/EventNoteRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";

/**
 * Tipos de acción que el motor entiende.
 * - RESPONDER: contesta con response_template
 * - ESCALAR_HUMANO: notifica al doctor por WhatsApp
 * - GESTIONAR_CITA: pide cancelar/cambiar cita existente
 * - FINALIZAR_CONVERSACION: cierra la sesión
 */
const ACTION_TYPES = [
  { value: "RESPONDER",                 label: "Responder con texto",            icon: <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 16 }} />, color: "#6366f1",
    descr: "El chatbot responde con el mensaje que definas." },
  { value: "ESCALAR_HUMANO",            label: "Escalar a humano",               icon: <WarningAmberRoundedIcon sx={{ fontSize: 16 }} />,       color: "#ef4444",
    descr: "Notifica al doctor por WhatsApp para que tome el chat." },
  { value: "GESTIONAR_CITA",            label: "Gestionar cita existente",       icon: <EventNoteRoundedIcon sx={{ fontSize: 16 }} />,          color: "#f59e0b",
    descr: "Cancelar o reagendar una cita ya existente del paciente." },
  { value: "FINALIZAR_CONVERSACION",    label: "Finalizar conversación",         icon: <CancelRoundedIcon sx={{ fontSize: 16 }} />,             color: "#94a3b8",
    descr: "Despide al paciente y cierra la sesión." },
];

function actionMeta(type) {
  return ACTION_TYPES.find((a) => a.value === type) || ACTION_TYPES[0];
}

function genIntentId() {
  return `intent_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export default function IntentsTab({ config, onSave, saving }) {
  const toolsConfig = config?.tools_config || {};
  const intents = useMemo(() => toolsConfig.intents || [], [toolsConfig]);

  const [editDialog, setEditDialog] = useState(false);
  const [editing, setEditing] = useState(null);  // intent siendo editado o null para nuevo
  const [form, setForm] = useState(null);
  const [keywordsRaw, setKeywordsRaw] = useState("");
  const [error, setError] = useState(null);

  const openNew = () => {
    setEditing(null);
    setForm({
      id: genIntentId(),
      name: "",
      enabled: true,
      action: { type: "RESPONDER" },
      detection: { keywords: [], patterns: [] },
      response_template: "",
    });
    setKeywordsRaw("");
    setError(null);
    setEditDialog(true);
  };

  const openEdit = (intent) => {
    setEditing(intent);
    setForm(JSON.parse(JSON.stringify(intent)));
    setKeywordsRaw((intent.detection?.keywords || []).join(", "));
    setError(null);
    setEditDialog(true);
  };

  const updateAndSave = (newIntents) => {
    const newToolsConfig = { ...toolsConfig, intents: newIntents };
    onSave({ tools_config: newToolsConfig });
  };

  const handleGuardar = () => {
    if (!form.name?.trim()) {
      setError("El nombre es obligatorio");
      return;
    }
    const keywords = keywordsRaw
      .split(",")
      .map((k) => k.trim().toLowerCase())
      .filter(Boolean);
    if (keywords.length === 0) {
      setError("Agrega al menos una palabra clave");
      return;
    }
    const intent = {
      ...form,
      name: form.name.trim(),
      detection: { ...(form.detection || {}), keywords, patterns: form.detection?.patterns || [] },
      response_template: form.response_template || "",
    };
    let next;
    if (editing) {
      next = intents.map((i) => (i.id === editing.id ? intent : i));
    } else {
      next = [...intents, intent];
    }
    updateAndSave(next);
    setEditDialog(false);
  };

  const handleDelete = (intent) => {
    if (!window.confirm(`¿Eliminar el intent "${intent.name}"?`)) return;
    updateAndSave(intents.filter((i) => i.id !== intent.id));
  };

  const handleToggle = (intent) => {
    updateAndSave(intents.map((i) =>
      i.id === intent.id ? { ...i, enabled: i.enabled === false ? true : !i.enabled } : i
    ));
  };


  return (
    <Box>
      {/* Header de la sección */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5, flexWrap: "wrap" }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 17, color: "#0f172a", letterSpacing: -0.2 }}>
            Intents conversacionales
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#64748b", lineHeight: 1.5 }}>
            Reglas que el chatbot ejecuta cuando detecta ciertas palabras clave en el mensaje del paciente.
          </Typography>
        </Box>
        <Button
          variant="contained" startIcon={<AddRoundedIcon />}
          onClick={openNew} disabled={saving}
          sx={{
            textTransform: "none", fontWeight: 700, borderRadius: 2,
            background: "linear-gradient(135deg, #a855f7 0%, #6366f1 100%)",
            boxShadow: "0 4px 14px rgba(168,85,247,0.4)",
            px: 2.5,
          }}
        >
          Nuevo intent
        </Button>
      </Box>

      {/* Lista o empty */}
      {intents.length === 0 ? (
        <Paper elevation={0} sx={{
          p: 5, borderRadius: 3, textAlign: "center",
          background: "#fff", border: "1px solid #e2e8f0",
        }}>
          <BoltRoundedIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 1.5 }} />
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#0f172a", mb: 0.5 }}>
            Sin intents configurados
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#64748b", mb: 2.5, maxWidth: 480, mx: "auto", lineHeight: 1.5 }}>
            Sin intents el chatbot responde 100% con su personalidad y base de conocimiento.
            Los intents te dejan disparar acciones específicas (agendar cita, escalar a humano, etc.) cuando
            detecta ciertas palabras clave.
          </Typography>
          <Button
            variant="contained" startIcon={<AddRoundedIcon />} onClick={openNew}
            sx={{
              textTransform: "none", fontWeight: 700, borderRadius: 2,
              background: "linear-gradient(135deg, #a855f7 0%, #6366f1 100%)",
              boxShadow: "0 4px 14px rgba(168,85,247,0.4)", px: 3,
            }}
          >
            Crear primer intent
          </Button>
        </Paper>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {intents.map((intent) => {
            const meta = actionMeta(intent.action?.type);
            const disabled = intent.enabled === false;
            const keywords = intent.detection?.keywords || [];
            return (
              <Paper key={intent.id} elevation={0} sx={{
                p: 0, borderRadius: 2.5,
                background: "#fff", border: "1px solid #e2e8f0",
                overflow: "hidden",
                opacity: disabled ? 0.55 : 1,
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute", top: 0, left: 0, bottom: 0,
                  width: 3, background: meta.color,
                },
              }}>
                <Box sx={{ p: 1.75, pl: 2.25, display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                  {/* Action icon */}
                  <Box sx={{
                    width: 32, height: 32, borderRadius: 1.5,
                    background: meta.color + "18", color: meta.color,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}>
                    {meta.icon}
                  </Box>

                  {/* Body */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.4, flexWrap: "wrap" }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0f172a", letterSpacing: -0.1 }}>
                        {intent.name || "Sin nombre"}
                      </Typography>
                      <Chip
                        size="small" label={meta.label}
                        sx={{
                          height: 18, fontSize: 10, fontWeight: 700,
                          backgroundColor: meta.color + "15", color: meta.color,
                          border: "none",
                        }}
                      />
                      {disabled && (
                        <Chip size="small" label="Deshabilitado" sx={{
                          height: 18, fontSize: 10, fontWeight: 700,
                          backgroundColor: "#f1f5f9", color: "#64748b", border: "none",
                        }}/>
                      )}
                    </Box>

                    {/* Keywords */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.4, mb: 0.85 }}>
                      {keywords.slice(0, 8).map((kw, i) => (
                        <Box key={i} sx={{
                          px: 0.8, py: 0.15, borderRadius: 0.85,
                          background: "#f1f5f9", border: "1px solid #e2e8f0",
                          fontSize: 10.5, color: "#475569", fontWeight: 600,
                        }}>
                          {kw}
                        </Box>
                      ))}
                      {keywords.length > 8 && (
                        <Box sx={{
                          px: 0.8, py: 0.15, borderRadius: 0.85,
                          background: "#eff6ff", border: "1px solid #bfdbfe",
                          fontSize: 10.5, color: "#2563eb", fontWeight: 700,
                        }}>
                          +{keywords.length - 8} más
                        </Box>
                      )}
                    </Box>

                    {/* Response template */}
                    {intent.response_template && (
                      <Typography sx={{
                        fontSize: 12, color: "#64748b", fontStyle: "italic",
                        overflow: "hidden", textOverflow: "ellipsis",
                        display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical",
                      }}>
                        💬 "{intent.response_template}"
                      </Typography>
                    )}
                  </Box>

                  {/* Acciones */}
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                    <Tooltip title={disabled ? "Habilitar" : "Deshabilitar"}>
                      <Switch
                        size="small"
                        checked={intent.enabled !== false}
                        onChange={() => handleToggle(intent)}
                        disabled={saving}
                      />
                    </Tooltip>
                    <Box sx={{ display: "flex", gap: 0.25 }}>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => openEdit(intent)} disabled={saving} sx={{ color: "#6366f1" }}>
                          <EditRoundedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton size="small" onClick={() => handleDelete(intent)} disabled={saving}
                          sx={{ color: "#94a3b8", "&:hover": { color: "#ef4444" } }}>
                          <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}

      {/* Diálogo crear/editar */}
      <Dialog
        open={editDialog} onClose={() => !saving && setEditDialog(false)}
        fullWidth maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <Box sx={{
          background: "linear-gradient(135deg, #1e3a5f, #2d5a87)",
          color: "#fff", px: 2.5, py: 1.75,
          display: "flex", alignItems: "center", gap: 1.25,
        }}>
          <BoltRoundedIcon sx={{ fontSize: 20 }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 15 }}>
              {editing ? "Editar intent" : "Nuevo intent"}
            </Typography>
            <Typography sx={{ fontSize: 12, opacity: 0.78 }}>
              Define qué hace el chatbot cuando detecta palabras clave
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setEditDialog(false)} sx={{ color: "#fff" }} disabled={saving}>
            <HighlightOffRoundedIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          {form && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Collapse in={!!error}>
                {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}
              </Collapse>

              <TextField
                label="Nombre del intent" fullWidth size="small" required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ej: Agendar cita"
              />

              <Box>
                <FormControl fullWidth size="small">
                  <InputLabel>Acción</InputLabel>
                  <Select
                    label="Acción" value={form.action?.type || "RESPONDER"}
                    onChange={(e) => setForm({ ...form, action: { ...form.action, type: e.target.value } })}
                  >
                    {ACTION_TYPES.map((a) => (
                      <MenuItem key={a.value} value={a.value}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ color: a.color, display: "flex" }}>{a.icon}</Box>
                          {a.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography sx={{ fontSize: 11.5, color: "#64748b", mt: 0.5, ml: 0.5 }}>
                  {actionMeta(form.action?.type).descr}
                </Typography>
              </Box>

              <TextField
                label="Palabras clave (separadas por coma)"
                fullWidth multiline rows={3} size="small" required
                value={keywordsRaw}
                onChange={(e) => setKeywordsRaw(e.target.value)}
                placeholder="agendar, cita, reservar, quiero cita, necesito cita"
                helperText="El chatbot dispara este intent cuando detecta cualquiera de estas frases en el mensaje del paciente."
              />

              <TextField
                label="Mensaje de respuesta"
                fullWidth multiline rows={3} size="small"
                value={form.response_template || ""}
                onChange={(e) => setForm({ ...form, response_template: e.target.value })}
                placeholder="Perfecto, te ayudo a agendar tu cita. ¿Para qué fecha te gustaría?"
                helperText="Se envía cuando se dispara el intent (todas las acciones lo usan)."
              />

              {/* Campos específicos por tipo de acción */}
              {form.action?.type === "ESCALAR_HUMANO" && (
                <TextField
                  label="Mensaje al doctor (notificación)"
                  fullWidth size="small"
                  value={form.action?.escalation_message || ""}
                  onChange={(e) => setForm({
                    ...form,
                    action: { ...form.action, escalation_message: e.target.value },
                  })}
                  placeholder="Un paciente necesita hablar con usted urgentemente."
                />
              )}

              <FormControlLabel
                control={<Switch
                  checked={form.enabled !== false}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                />}
                label={<Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                  Habilitado (el chatbot detectará este intent)
                </Typography>}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEditDialog(false)} disabled={saving}
            sx={{ textTransform: "none", color: "#64748b" }}>
            Cancelar
          </Button>
          <Button
            variant="contained" onClick={handleGuardar} disabled={saving}
            sx={{
              textTransform: "none", fontWeight: 700, borderRadius: 2,
              background: "linear-gradient(135deg, #a855f7, #6366f1)",
              boxShadow: "0 4px 12px rgba(168,85,247,0.35)", px: 3,
            }}
          >
            {editing ? "Guardar cambios" : "Crear intent"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
